/**
 * Filter interval keys used across the application
 */
export const FILTER_INTERVAL_KEYS = {
  ALL_TIME: 'all_time',
  ONE_DAY: 'one_day',
  ONE_WEEK: 'one_week',
  TWO_WEEKS: 'two_weeks',
  ONE_MONTH: 'one_month',
  THREE_MONTH: 'three_month',
  SIX_MONTH: 'six_month',
  ONE_YEAR: 'one_year',
  FIVE_YEAR: 'five_year',
  YTD: 'YTD',
};

export const FRONT_TO_API_INTERVAL_KEY_MAP = {
  [FILTER_INTERVAL_KEYS.ONE_DAY]: '1D',
  [FILTER_INTERVAL_KEYS.ONE_WEEK]: '1W',
  [FILTER_INTERVAL_KEYS.TWO_WEEKS]: '2W',
  [FILTER_INTERVAL_KEYS.ONE_MONTH]: '1M',
  [FILTER_INTERVAL_KEYS.THREE_MONTH]: '3M',
  [FILTER_INTERVAL_KEYS.SIX_MONTH]: '6M',
  [FILTER_INTERVAL_KEYS.ONE_YEAR]: '1Y',
  [FILTER_INTERVAL_KEYS.FIVE_YEAR]: '5Y',
  [FILTER_INTERVAL_KEYS.YTD]: 'YTD',
  [FILTER_INTERVAL_KEYS.ALL_TIME]: 'ALL',
};

/**
 * Get the start date of the requested interval
 * @param {Dayjs} now - The current date
 * @param {Object} interval - The interval object
 * Example : {
 *   key: FILTER_INTERVAL_KEYS.FIVE_YEAR,
 *   displayName: this.$t('five_year'),
 *   unit: 'year',
 *   amount: 5,
 * }
 * @returns {Dayjs|null} The start date of the requested interval
 */
export function getRequestedIntervalStartDayjs(now, interval) {
  if (!interval) return null;

  if (interval.unit) {
    return now.subtract(interval.amount, interval.unit);
  }

  if (interval.startOf) {
    return now.startOf(interval.startOf);
  }

  return null;
}

/**
 * Resolve the API interval key based on the current graph filter interval, the listed date, and the show performance since listed flag
 * @param {Object} interval - The interval object
 * @param {string} goPublicDate - The listed date
 * @param {boolean} showPerformanceSincePublic - The show performance since listed flag
 * @param {Dayjs} $dayjs - The dayjs instance
 * @returns {string|null} The API interval key
 */
export function resolveApiIntervalKey({
  interval,
  goPublicDate,
  showPerformanceSincePublic,
  $dayjs,
}) {
  const baseApiKey = FRONT_TO_API_INTERVAL_KEY_MAP[interval?.key];

  if (!baseApiKey) return null;

  // If user didn't ask for "since public", just use the selected interval
  if (!showPerformanceSincePublic) return baseApiKey;

  // If we don't have a listed date, fallback to selected interval
  if (!goPublicDate) return baseApiKey;

  // If the selected interval is already sinceBecamePublic, keep it
  if (baseApiKey === 'sinceBecamePublic') return baseApiKey;

  const now = $dayjs();
  const intervalStart = getRequestedIntervalStartDayjs(now, interval);
  if (!intervalStart) return baseApiKey;

  const listed = $dayjs(goPublicDate);

  // Key rule:
  // If public date is AFTER the interval start, use sinceBecamePublic interval series
  return listed.isAfter(intervalStart) ? 'sinceBecamePublic' : baseApiKey;
}
